import dayjs from 'dayjs';
import { Box, Typography } from '@mui/material';
import { ActivityStatus } from 'src/types/dto/activity.dto';
import { AppointmentDTO } from 'src/types/dto/appointment.dto';
import { formatAppointmentShortDateInZone } from 'src/utils/timezone';

interface Props {
  scheduledDate?: string | null | undefined;
  status?: ActivityStatus | null | undefined;
  timezoneId?: string | null | undefined;
  appointments?: Array<Pick<AppointmentDTO, 'from' | 'to' | 'type' | 'timezoneId'>> | null | undefined;
}

function resolveTimezoneId(
  timezoneId: string | null | undefined,
  appointments?: Array<Pick<AppointmentDTO, 'timezoneId'>> | null,
): string {
  const explicit = timezoneId?.trim();
  if (explicit) {
    return explicit;
  }

  const fromAppointment = appointments
    ?.map((appointment) => appointment.timezoneId?.trim())
    .find(Boolean);

  return fromAppointment || 'UTC';
}

export function formatRangeFromAppointments(
  appointments: Array<Pick<AppointmentDTO, 'from' | 'to' | 'type' | 'timezoneId'>>,
  timezoneId?: string | null,
): string | false {
  const sorted = appointments
    .filter((appointment) => appointment.type !== 'blocker')
    .sort((a, b) => dayjs(a.from).diff(dayjs(b.from)));

  if (sorted.length === 0) {
    return false;
  }

  const tz = resolveTimezoneId(timezoneId, sorted);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const startLabel = first?.from ? formatAppointmentShortDateInZone(first.from, tz) : '';
  if (!startLabel) {
    return false;
  }

  const endInstant = last?.to ?? last?.from;
  if (!endInstant) {
    return startLabel;
  }

  const endLabel = formatAppointmentShortDateInZone(endInstant, tz);
  if (!endLabel || startLabel === endLabel) {
    return startLabel;
  }

  return `${startLabel} – ${endLabel}`;
}

export const Scheduled = ({ scheduledDate, appointments, timezoneId }: Props) => {
  const tz = resolveTimezoneId(timezoneId, appointments ?? undefined);

  let date: string | false;
  if (scheduledDate) {
    date = formatAppointmentShortDateInZone(scheduledDate, tz) || false;
  } else if (appointments && appointments.length > 0) {
    date = formatRangeFromAppointments(appointments, timezoneId);
  } else {
    date = false;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <Typography variant="body2" color="text.primary">
        {date || '-'}
      </Typography>
    </Box>
  );
};
